import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';

import { TextSpan } from '../TextSpan';
import { colorPalette, colors } from '../../../app/theme';

const styles = (theme) => ({
  root: {
    marginBottom: 0,
    marginTop: 56,
    paddingTop: 56,
    borderTop: `1px solid ${colorPalette.gray[30]}`,
    [theme.breakpoints.down('sm')]: {
      marginTop: 48,
      paddingTop: 48,
    },
  },
  noTopMargin: {
    marginTop: 0,
    paddingTop: 0,
  },
  noBorder: {
    borderTop: '0 none',
  },
  firstColumn: {
    paddingRight: 16,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  secondColumn: {
    paddingLeft: 16,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  sectionHeader: {
    width: '100%',
    marginBottom: 32,
  },
  sectionHeaderTitles: {},
  sectionHeaderActions: {},
  sectionTitle: {
    marginBottom: 8,
    color: colors.black,
    fontWeight: 'bold',
  },
  sectionSubTitle: {
    color: colors.black_light,
    maxWidth: 608,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'none',
    },
  },
});

export const SECTION_LAYOUT = {
  ONE_COLUMN: 'one',
  TWO_COLUMNS: 'two',
  ONE_THIRD_TWO_THIRDS: 'one_third_two_thirds',
  TWO_THIRDS_ONE_THIRD: 'two_thirds_one_third',
};

const twoColumnsLayouts = {
  [SECTION_LAYOUT.TWO_COLUMNS]: [6, 6],
  [SECTION_LAYOUT.ONE_THIRD_TWO_THIRDS]: [4, 8],
  [SECTION_LAYOUT.TWO_THIRDS_ONE_THIRD]: [8, 4],
};
const getColumnsLayout = (layout) => twoColumnsLayouts[layout] || twoColumnsLayouts[SECTION_LAYOUT.TWO_COLUMNS];

const renderTitle = (title, classes, isXs) => (
  <header>
    <TextSpan type={isXs ? 'h6' : 'h5'} className={classes.sectionTitle}>
      {title}
    </TextSpan>
  </header>
);

const renderSubTitle = (subTitle, classes, isXs) => (
  <TextSpan type={isXs ? 'subtitle2' : 'subtitle1'} className={classes.sectionSubTitle}>
    {subTitle}
  </TextSpan>
);

const SectionWrapped = (props) => {
  const { title, subTitle, headerAction, noBorder, noTopMargin, children, layout, classes, width } = props;
  const isXs = isWidthDown('xs', width);
  const hasSectionHeader = title != null || subTitle != null;

  const sectionRootClasses = classNames(classes.root, {
    [classes.noBorder]: noBorder,
    [classes.noTopMargin]: noTopMargin,
  });

  if (Object.keys(twoColumnsLayouts).includes(layout)) {
    const [firstColumn, secondColumn] = getColumnsLayout(layout);

    return (
      <section className={sectionRootClasses}>
        <Grid container>
          <Grid item xs={12} md={firstColumn} className={classes.firstColumn}>
            {hasSectionHeader && (
              <Grid container className={classes.sectionHeader}>
                <Grid item xs={12} className={classes.sectionHeaderTitles}>
                  {title && renderTitle(title, classes, isXs)}
                  {subTitle && renderSubTitle(subTitle, classes, isXs)}
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} md={secondColumn} className={classes.secondColumn}>
            <Grid container>
              {headerAction && (
                <Grid item className={classes.sectionHeaderActions}>
                  {headerAction}
                </Grid>
              )}
              <Grid item xs={12}>
                {children}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </section>
    );
  }

  return (
    <section className={sectionRootClasses}>
      {hasSectionHeader && (
        <Grid container className={classes.sectionHeader}>
          <Grid item className={classes.sectionHeaderTitles}>
            {title && renderTitle(title, classes, isXs)}
            {subTitle && renderSubTitle(subTitle, classes, isXs)}
          </Grid>
          {headerAction && (
            <Grid item className={classes.sectionHeaderActions}>
              {headerAction}
            </Grid>
          )}
        </Grid>
      )}

      {children}
    </section>
  );
};

SectionWrapped.propTypes = {
  title: PropTypes.node,
  subTitle: PropTypes.node,
  headerAction: PropTypes.node,
  noBorder: PropTypes.bool,
  noTopMargin: PropTypes.bool,
  children: PropTypes.node,
  layout: PropTypes.oneOf(Object.values(SECTION_LAYOUT)),
  classes: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
};

SectionWrapped.defaultProps = {
  title: null,
  subTitle: null,
  headerAction: null,
  noBorder: false,
  noTopMargin: false,
  children: null,
  layout: SECTION_LAYOUT.ONE_COLUMN,
};

export default withWidth()(withStyles(styles)(SectionWrapped));
